import React from "react";

import { metricName } from "../../../whiteLabel/metricKeys";
import { CONTENT_H } from "../../../whiteLabel/pageMetrics";
import SourcesByMetric, {
  SourceGroup,
  splitSourceGroups,
} from "../components/SourcesByMetric";
import WhiteLabelCard from "../components/WhiteLabelCard";
import WhiteLabelPage from "../WhiteLabelPage/WhiteLabelPage";
import { WhiteLabelPageProps } from "../types";

import classes from "./SourcesDisclaimerPage.module.css";

/** Height reserved for the disclaimer card on whichever page carries it. */
const DISCLAIMER_H = 210;
/** Header block height, with and without the original-language note. */
const TITLE_H_PLAIN = 56;
const TITLE_H_WITH_NOTE = 78;

const SourcesDisclaimerPage = ({
  data,
  theme,
  lang,
  t,
  pageNumber,
}: WhiteLabelPageProps): JSX.Element => {
  const overview = data?.PAGES?.find(
    (page: any) => page.PAGE_TYPE === "OVERVIEW"
  );

  // Risk metrics carry their own SOURCES, and the mock-up lists them alongside the
  // rest, so both collections feed the citation groups.
  const metrics: any[] = overview?.ORGANS
    ? Object.values(overview.ORGANS).flatMap((organ: any) => [
        ...(organ.METRICS ?? []),
        ...(organ.RISK_METRICS ?? []),
      ])
    : [];

  const groups: SourceGroup[] = metrics
    .map((metric) => ({
      // Same localized name as the page-1 bar titles.
      title: metricName(metric.METRIC_TYPE, t.metrics, metric.TITLE1),
      items: (metric.SOURCES ?? [])
        .map((source: any) => source?.VALUES?.[0]?.VALUE)
        .filter(Boolean) as string[],
    }))
    .filter((group) => group.items.length > 0);

  // Only the final page of the report may spill, so a variable page count here
  // cannot desynchronize any later page's footer numbering.
  // The note explains why the citations are not in the reader's language, so it is
  // redundant on the English report — the citations are already English there.
  const showLanguageNote = lang !== "en";

  const { fitted, overflow } = splitSourceGroups(
    groups,
    CONTENT_H -
      (showLanguageNote ? TITLE_H_WITH_NOTE : TITLE_H_PLAIN) -
      DISCLAIMER_H
  );

  // The disclaimer no longer comes from COVER.DESCRIPTION: the backend served it in
  // English only, so on a ru/zh report it was the one block of copy that stayed
  // English. It now lives in the dictionary, with {company} filled from the theme.
  const disclaimerBody = t.sources.disclaimerBody.replace(
    "{company}",
    theme.companyName
  );

  const disclaimer = (
    <div className={classes.disclaimer}>
      <WhiteLabelCard>
        <div className={classes.disclaimerLabel}>
          {t.sources.disclaimerLabel}
        </div>
        <div className={classes.disclaimerText}>{disclaimerBody}</div>
      </WhiteLabelCard>
    </div>
  );

  return (
    <>
      <WhiteLabelPage
        theme={theme}
        lang={lang}
        brandLabelTemplate={t.footer.brandLabel}
      sectionName={t.sections.sourcesDisclaimer}
        pageNumber={pageNumber}
        dataAgentTarget="wl-sources-disclaimer"
      >
        <div className={classes.pageTitle}>{t.sources.heading}</div>
        {showLanguageNote && (
          <div className={classes.subtitle}>
            {t.sources.originalLanguageNote}
          </div>
        )}
        <div className={classes.sources}>
          <SourcesByMetric groups={fitted} />
        </div>
        {overflow.length === 0 && disclaimer}
      </WhiteLabelPage>

      {overflow.length > 0 && (
        <WhiteLabelPage
          theme={theme}
          lang={lang}
          brandLabelTemplate={t.footer.brandLabel}
      sectionName={t.sections.sourcesDisclaimer}
          pageNumber={pageNumber + 1}
          dataAgentTarget="wl-sources-disclaimer-2"
        >
          <div className={classes.pageTitle}>{t.sources.heading}</div>
        {showLanguageNote && (
          <div className={classes.subtitle}>
            {t.sources.originalLanguageNote}
          </div>
        )}
          <div className={classes.sources}>
            <SourcesByMetric groups={overflow} />
          </div>
          {disclaimer}
        </WhiteLabelPage>
      )}
    </>
  );
};

export default SourcesDisclaimerPage;
